import { useCallback, useEffect, useState } from "react";

import { ShipmentChip } from "@/components/chips";
import { Pager, Select } from "@/routes/orders";
import { Table, Tile } from "@/routes/payments";
import * as admin from "@/lib/api/admin";
import { ApiError } from "@/lib/api-client";
import type {
  ApiShipment,
  ApiWebhookEvent,
  ShipmentStatus,
  ShipmentSummary,
} from "@/lib/api/types";
import { cn, formatDateTime, formatMinor } from "@/lib/utils";

/**
 * Shipments — where a parcel is, and which ones need a person.
 *
 *   Shipments   every booking, and what the courier last said about it
 *   Exceptions  the ones that will cost money if nobody acts
 *   Webhooks    whether iCarry is actually reaching us
 *
 * The Exceptions tab is the reason this screen exists rather than a column on
 * the orders table. An NDR is consolidated and delivered **once a day**: if
 * nobody acts on it, the parcel is reattempted twice and then comes back to us
 * at our cost, and nothing else in the system goes visibly wrong in the
 * meantime. A weight dispute is money already being deducted, and it is also
 * direct evidence that a product's stored weight is wrong.
 *
 * The webhooks tab shares the payments screen's audit table — `WebhookEvent` is
 * provider-agnostic, so both integrations' callbacks land in one log. It
 * carries more weight here than it does there: iCarry does not sign its
 * callbacks and offers no retry contract, so a run of rejections is the only
 * signal that the panel's API key was regenerated.
 */

type Tab = "shipments" | "exceptions" | "webhooks";

const TABS: { id: Tab; label: string }[] = [
  { id: "shipments", label: "Shipments" },
  { id: "exceptions", label: "Exceptions" },
  { id: "webhooks", label: "Courier webhooks" },
];

export function ShipmentsRoute() {
  const [tab, setTab] = useState<Tab>("shipments");
  const [summary, setSummary] = useState<ShipmentSummary | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void admin
      .fetchShipmentSummary(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setSummary(data);
      })
      .catch(() => {
        // A failed summary must not take the tables with it. The tiles simply
        // do not render; the screen is still fully usable without them.
      });

    return () => controller.abort();
  }, [reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-[20px] font-semibold text-ink">Shipments</h1>
        <p className="mt-1 text-[13px] text-muted">
          Every parcel booked with iCarry, and what the courier last told us about it.
        </p>
      </header>

      {summary ? <Summary summary={summary} /> : null}

      <div className="mb-4 flex gap-1 border-b border-line">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-[13px] font-semibold transition-colors",
              tab === item.id
                ? "border-ink text-ink"
                : "border-transparent text-muted hover:text-ink",
            )}
          >
            {item.label}
            {item.id === "exceptions" && summary && summary.ndr + summary.failed > 0 ? (
              <span className="ml-1.5 rounded-full bg-bad-tint px-1.5 py-0.5 text-[10px] font-bold text-bad">
                {summary.ndr + summary.failed}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === "shipments" ? <ShipmentsTable onChanged={refresh} /> : null}
      {tab === "exceptions" ? <ExceptionsTable onChanged={refresh} /> : null}
      {tab === "webhooks" ? <WebhooksTable /> : null}
    </div>
  );
}

function Summary({ summary }: { summary: ShipmentSummary }) {
  return (
    <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <Tile label="In transit" value={String(summary.inTransit)} strong />
      <Tile label="Delivered · 7d" value={String(summary.deliveredWeek)} />
      <Tile
        label="NDR"
        value={String(summary.ndr)}
        {...(summary.ndr > 0
          ? {
              /**
               * The one number nobody would think to go and look at. An NDR
               * arrives once a day and the parcel comes back at our cost if it
               * is not answered.
               */
              alert: "needs an answer today",
            }
          : {})}
      />
      <Tile
        label="Weight disputes"
        value={String(summary.weightDisputes)}
        {...(summary.weightDisputes > 0 ? { alert: "a product weight is wrong" } : {})}
      />
      <Tile
        label="Failed bookings"
        value={String(summary.failed)}
        {...(summary.failed > 0 ? { alert: "never reached the courier" } : {})}
      />
    </div>
  );
}

// ─── Shipments ───────────────────────────────────────────────────────────────

const SHIPMENT_STATUSES: ShipmentStatus[] = [
  "DRAFT",
  "BOOKED",
  "PICKED_UP",
  "IN_TRANSIT",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "UNDELIVERED",
  "RTO",
  "RETURNED",
  "CANCELLED",
  "FAILED",
];

/** Everything that will cost money if nobody looks at it. */
const EXCEPTIONS: ShipmentStatus[] = ["UNDELIVERED", "RTO", "FAILED", "DRAFT"];

function ShipmentsTable({ onChanged }: { onChanged: () => void }) {
  return <List statuses={null} onChanged={onChanged} empty="No shipments match those filters." />;
}

function ExceptionsTable({ onChanged }: { onChanged: () => void }) {
  return (
    <List
      statuses={EXCEPTIONS}
      onChanged={onChanged}
      empty="Nothing needs attention. Every parcel is either moving or delivered."
    />
  );
}

/**
 * One table, two tabs.
 *
 * The Exceptions tab is this list with a fixed status filter rather than a
 * second component — the rows are identical, and two components rendering the
 * same row is how one of them stops showing the retry button.
 */
function List({
  statuses,
  onChanged,
  empty,
}: {
  statuses: ShipmentStatus[] | null;
  onChanged: () => void;
  empty: string;
}) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ShipmentStatus | "">("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<ApiShipment[] | null>(null);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        /**
         * The exceptions tab asks for its statuses one at a time and merges
         * them, because the API's filter takes a single status. Four small
         * queries beat widening the endpoint's contract for one screen.
         */
        const queries = statuses ?? [status || undefined];

        const results = await Promise.all(
          queries.map((value) =>
            admin.fetchShipments(
              {
                page: statuses ? 1 : page,
                status: value as ShipmentStatus | undefined,
                search: search.trim() || undefined,
              },
              controller.signal,
            ),
          ),
        );

        if (controller.signal.aborted) return;

        const merged = results.flatMap((result) => result.data);
        merged.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

        setRows(merged);
        setPages(statuses ? 1 : Math.max(1, results[0]?.meta.pages ?? 1));
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load shipments.");
        }
      }
    })();

    return () => controller.abort();
  }, [page, status, search, statuses, reload]);

  async function act(
    id: string,
    action: (id: string) => Promise<ApiShipment>,
    failure: string,
  ): Promise<void> {
    setBusy(id);
    setError(null);

    try {
      await action(id);
      setReload((n) => n + 1);
      onChanged();
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : failure);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {statuses ? null : (
        <div className="mb-4 flex flex-wrap gap-2">
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="AWB, booking reference or order number…"
            className="w-[280px] rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] text-ink placeholder:text-faint"
          />
          <Select
            value={status}
            onChange={(value) => {
              setStatus(value as ShipmentStatus | "");
              setPage(1);
            }}
            options={[
              { value: "", label: "All statuses" },
              ...SHIPMENT_STATUSES.map((s) => ({
                value: s,
                label: s.replace(/_/g, " ").toLowerCase(),
              })),
            ]}
          />
        </div>
      )}

      <Table
        error={error}
        rows={rows}
        empty={empty}
        head={["Order", "Courier", "Status", "Parcel", "Updated", ""]}
        render={(shipment: ApiShipment) => (
          <tr key={shipment.id} className="border-b border-line align-top last:border-0">
            <td className="px-4 py-3">
              <span className="block font-medium text-ink">
                {shipment.order?.orderNumber ?? shipment.bookingRef}
              </span>
              <span className="block text-[11.5px] text-faint">
                {shipment.order?.customerEmail ?? shipment.bookingRef}
              </span>
            </td>

            <td className="px-4 py-3">
              <span className="block text-[12.5px] text-body">{shipment.courierName ?? "—"}</span>
              {shipment.awb ? (
                <span className="block font-mono text-[11.5px] text-faint">{shipment.awb}</span>
              ) : null}
            </td>

            <td className="px-4 py-3">
              <ShipmentChip status={shipment.status} />
              {/* iCarry's own prose — concatenated English sentences with no
                  code. Useless to a customer, first thing an operator needs,
                  which is why the two serializers differ. */}
              {shipment.failureReason ? (
                <p className="mt-1 max-w-[260px] text-[11.5px] leading-relaxed text-bad">
                  {shipment.failureReason}
                </p>
              ) : null}
              {shipment.etaAt && shipment.status !== "DELIVERED" ? (
                <p className="mt-1 text-[11.5px] text-faint">
                  Expected {formatDateTime(shipment.etaAt)}
                </p>
              ) : null}
            </td>

            <td className="px-4 py-3 text-[12px] text-muted tabular-nums">
              {shipment.parcel.weightGrams} g
              <span className="block text-[11.5px] text-faint">
                {shipment.parcel.lengthMm}×{shipment.parcel.breadthMm}×{shipment.parcel.heightMm} mm
              </span>
              <span className="block text-[11.5px] text-faint">
                {formatMinor(shipment.parcel.declared.minor)} declared
              </span>
            </td>

            <td className="px-4 py-3 text-[12px] text-muted">
              {formatDateTime(shipment.updatedAt)}
            </td>

            <td className="px-4 py-3 text-right">
              <div className="flex justify-end gap-1.5">
                {/* A DRAFT never reached the courier: retrying the booking is
                    the useful action, not polling for a status that cannot
                    exist. */}
                {shipment.status === "DRAFT" || shipment.status === "FAILED" ? (
                  <Action
                    label="Retry"
                    busy={busy === shipment.id}
                    onClick={() =>
                      void act(shipment.id, admin.retryShipment, "Could not retry the booking.")
                    }
                  />
                ) : null}

                {shipment.providerShipmentId ? (
                  <Action
                    label="Sync"
                    busy={busy === shipment.id}
                    onClick={() =>
                      void act(shipment.id, admin.syncShipment, "Could not reach the courier.")
                    }
                  />
                ) : null}

                {shipment.status !== "DELIVERED" &&
                shipment.status !== "CANCELLED" &&
                shipment.status !== "RETURNED" ? (
                  <Action
                    label="Cancel"
                    tone="bad"
                    busy={busy === shipment.id}
                    onClick={() =>
                      void act(shipment.id, admin.cancelShipment, "Could not cancel the booking.")
                    }
                  />
                ) : null}
              </div>
            </td>
          </tr>
        )}
      />

      {statuses ? null : <Pager page={page} pages={pages} onChange={setPage} />}
    </>
  );
}

function Action({
  label,
  onClick,
  busy,
  tone,
}: {
  label: string;
  onClick: () => void;
  busy: boolean;
  tone?: "bad";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        "rounded-lg border px-2.5 py-1 text-[12px] font-semibold transition-colors disabled:opacity-50",
        tone === "bad"
          ? "border-line text-bad hover:bg-bad-tint"
          : "border-line text-body hover:bg-canvas hover:text-ink",
      )}
    >
      {busy ? "…" : label}
    </button>
  );
}

// ─── Courier webhooks ────────────────────────────────────────────────────────

/**
 * The courier's own callbacks, out of the shared `WebhookEvent` log.
 *
 * Filtered by type prefix rather than by a provider parameter, because the
 * endpoint's filter is `type` and every courier row is written as
 * `ICARRY_STATUS` / `ICARRY_NDR` / `ICARRY_WEIGHT`.
 *
 * `signatureValid: false` matters more here than on the payments screen. iCarry
 * authenticates with our API key echoed back in the body, so a run of
 * rejections means the key in the panel and the key in this deploy have
 * diverged — and nothing else would show it, because a rejected callback is
 * answered 200 and never retried.
 */
const ICARRY_TYPES = ["ICARRY_STATUS", "ICARRY_NDR", "ICARRY_WEIGHT"];

function WebhooksTable() {
  const [rows, setRows] = useState<ApiWebhookEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const results = await Promise.all(
          ICARRY_TYPES.map((type) => admin.fetchWebhooks({ type }, controller.signal)),
        );

        if (controller.signal.aborted) return;

        const merged = results.flatMap((result) => result.data);
        merged.sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));

        setRows(merged);
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load callbacks.");
        }
      }
    })();

    return () => controller.abort();
  }, []);

  return (
    <Table
      error={error}
      rows={rows}
      empty="No courier callbacks yet. Check the webhook URLs in the iCarry panel."
      head={["Type", "Token", "Received", "Handled"]}
      render={(event: ApiWebhookEvent) => (
        <tr key={event.id} className="border-b border-line last:border-0">
          <td className="px-4 py-3 font-medium text-ink">{event.type}</td>
          <td className="px-4 py-3">
            {event.signatureValid ? (
              <span className="text-[12px] text-muted">Accepted</span>
            ) : (
              <span className="text-[12px] font-semibold text-bad">Rejected</span>
            )}
          </td>
          <td className="px-4 py-3 text-[12px] text-muted">{formatDateTime(event.receivedAt)}</td>
          <td className="px-4 py-3 text-[12px]">
            {event.error ? (
              <span className="text-bad">{event.error}</span>
            ) : event.processedAt ? (
              <span className="text-muted">Yes</span>
            ) : (
              /* Received and never handled: the handler threw. Nothing retries
                 it — a redelivery short-circuits on the body hash. */
              <span className="font-semibold text-warn">Not handled</span>
            )}
          </td>
        </tr>
      )}
    />
  );
}
